import {
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Checkbox,
  Stack,
} from "@mui/material";
import { Box } from "@mui/system";
import { useTranslation } from "react-i18next";
import { SuppliesDetailObject } from "../../../../core/models/suppliesDetail/suppliesDetail.model";
import { useEffect, useState } from "react";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ChangeEventDatePicker } from "../../../../core/models/bills/bills.model";
import ManagementsUpdateNewPowersTable from "./ManagementsUpdateNewPowersTable/ManagementsUpdateNewPowersTable";
import ButtonElement from "../../../UI/ButtonElement/ButtonElement";
import LoadingComponentComponent from "../../../UI/LoadingComponentComponent/LoadingComponentComponent";
import { NewPowers } from "../../../../core/models/managementsUpdate/managementsUpdatePowers/managementsUpdatePowers";
import { formatUpdatePowersRequestBody } from "../../../../utils/components/managementsUpdate/managementsUpdatePowers/managementsUpdatePowers.util";
import ManagementUploadFile from "../../../Managements/ManagementsUploadFile/ManagementsUploadFile";
import ManagementsCancelProcess from "../../../Managements/ManagementsCancelProcess/ManagementsCancelProcess";

interface ManagementsUpdateNewPowersProps {
  cupsDetail: SuppliesDetailObject;
  isLoading: boolean;
  handleSubmit: (incidenceText: string) => void;
  setFile: React.Dispatch<React.SetStateAction<File | null>>;
}

const ManagementsUpdateNewPowers = ({
  cupsDetail: { powers, rate },
  isLoading,
  handleSubmit,
  setFile,
}: ManagementsUpdateNewPowersProps) => {
  const { t } = useTranslation();
  const [checkBoxRadioValue, setCheckBoxRadioValue] = useState<string>("");
  const [isConsentAccepted, setIsConsentAccepted] = useState<boolean>(false);
  const [appliedDay, setAppliedDay] = useState<ChangeEventDatePicker | null>(
    null
  );
  const [notes, setNotes] = useState<string>("");
  const [hasErrorPowers, setHasErrorPowers] = useState<boolean>(false);
  const [newPowers, setNewPowers] = useState<NewPowers>({});
  const isSamePowersLength =
    powers.length === Object.values(newPowers).length &&
    !Object.values(newPowers).some((newPower) => !newPower.length);
  const isDisabled =
    !!!Object.values(newPowers)[0]?.length ||
    hasErrorPowers ||
    !isConsentAccepted ||
    !!!checkBoxRadioValue.length ||
    (checkBoxRadioValue === "selectDate" && !appliedDay) ||
    (!rate.includes("2") && !isSamePowersLength);

  useEffect(() => {
    setHasErrorPowers(checkPowersError(newPowers));
  }, [newPowers]);

  const appliedDayData = [
    {
      id: "asap",
      label: "Cuanto antes",
      value: "asap",
    },
    {
      id: "selectDate",
      label: "Seleccionar fecha",
      value: "selectDate",
    },
  ];

  const checkPowersError = (newPowers: NewPowers) =>
    Object.values(newPowers).some(
      (newPower, indexNumber, newPowersArray) =>
        !!indexNumber &&
        newPower.length &&
        +newPowersArray[indexNumber - 1] > +newPower
    );

  const [isModalOpen, setModalOpen] = useState(false);

  const openModal = () => {
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const getPowerMessageKey = (rate: string) => {
    if (rate.includes('2')) {
      return 'Gestiones.Tarifa2.0TD';
    } else if (rate.includes('3')) {
      return 'Gestiones.Tarifa3.0TD';
    } else if (rate.includes('6')) {
      return 'Gestiones.Tarifa6.1TD';
    } else {
      return 'Gestiones.TarifaDefault';
    }
  };

  const powerMessageKey = getPowerMessageKey(rate);
  const powerMessage = t(powerMessageKey);

  return (
    <form>
      <Stack component="div">
        <Stack>
          <Typography
            component="h3"
            variant="h3"
            paddingBottom={{ xs: "1.25rem", md: "1rem" }}
          >
            {t("Gestiones.Elige nuevas potencias")}
          </Typography>
        </Stack>
        <Stack>
          <Typography
            component="p"
            variant="body1"
            paddingBottom={{ xs: "2rem", md: "1rem" }}
          >
            {t('Gestiones.TarifaMessage', {
              rate,
              powerMessage
            })}
          </Typography>
        </Stack>
        <Stack>
          <Typography
            component="p"
            variant="body1"
            paddingBottom={{ xs: "2rem", md: "3.5rem" }}
          >
            {t(
              "Gestiones.Es importante que introduzca las nuevas potencias en orden ascendente"
            )}
          </Typography>
        </Stack>
        <Stack
          order={{ xs: "3", md: "4" }}
          paddingBottom={{ xs: "2rem", md: "3.5rem" }}
        >
          <ManagementsUpdateNewPowersTable
            powers={powers}
            newPowers={newPowers}
            setNewPowers={setNewPowers}
            isLoading={isLoading}
            rate={rate}
          />
          {isLoading && (
            <Box width={"100%"} padding={"1rem 0"}>
              <LoadingComponentComponent
                isLoading={isLoading}
                position="relative"
              />
            </Box>
          )}
        </Stack>
      </Stack>
      <Box component="div" paddingBottom={{ xs: "2rem", md: "3.5rem" }}>
        <Typography component="h3" variant="h3" paddingBottom="1.5rem">
          {t("Gestiones.Día de aplicación")}
        </Typography>
        <RadioGroup
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "1.5rem",
            paddingBottom: "1.5rem",
          }}
          className="powers-radio"
        >
          {appliedDayData.map(({ id, label, value }) => (
            <FormControlLabel
              key={id}
              value={value}
              label={t(`Gestiones.${label}`)}
              onChange={() => setCheckBoxRadioValue(value)}
              control={<Radio disableRipple />}
            />
          ))}
        </RadioGroup>
        <FormControl sx={{ gap: "0.5rem", alignItems: "start" }}>
          <FormLabel htmlFor="dates">
            <Typography fontWeight={600} variant="button">
              {t(`Gestiones.Fecha de aplicación`)}
              <span style={{ color: "red" }}> *</span>
            </Typography>
          </FormLabel>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
            <DatePicker
              disablePast
              onChange={setAppliedDay}
              disabled={checkBoxRadioValue !== "selectDate"}
              value={appliedDay}
              slotProps={{
                textField: {
                  InputProps: {
                    placeholder: t("Seleccionar"),
                    sx: {},
                    startAdornment: appliedDay && (
                      <IconButton
                        sx={{ position: "absolute", right: "2rem" }}
                        onClick={() => setAppliedDay(null)}
                      >
                        <span className="t-icon-close" />
                      </IconButton>
                    ),
                  },
                },
              }}
            />
          </LocalizationProvider>

          <Typography variant="caption" fontStyle={"italic"} marginTop={1}>
              {t(`Gestiones.Fecha de aplicación supeditada`)}
          </Typography>
        </FormControl>
      </Box>
      <Box component="div" paddingBottom={{ xs: "2rem", md: "3.5rem" }}>
        <Typography component="h3" variant="h3" paddingBottom="1.5rem">
          {t("Gestiones.Anexo")}
        </Typography>
        <Typography component="p" variant="body1">
          {t("Gestiones.Opcionalmente puede subir cualquier tipo de boletín")}
        </Typography>
        <ManagementUploadFile
          onFileUpload={(id, file) => {
            setFile(file);
          }}
        />
      </Box>
      <Box component="div" paddingBottom={{ xs: "2rem", md: "3.5rem" }}>
        <Typography component="h3" variant="h3" paddingBottom="1.5rem">
          {t("Gestiones.Nota")}
        </Typography>
        <Typography component="p" variant="body1" paddingBottom="1.5rem">
          {t("Gestiones.Opcionalmente puede dejarnos un mensaje")}
        </Typography>
        <FormControl
          sx={{
            display: "flex",
            gap: "1rem",
          }}
        >
          <FormLabel required sx={{ display: "flex", direction: "column" }}>
            <Typography component={"p"}>
              <Typography component={"span"} fontWeight={600}>
                {t("Gestiones.Mensaje")}
              </Typography>
              {` - ${t("Opcional")}`}
            </Typography>
          </FormLabel>
          <TextField
            multiline
            rows={10}
            onChange={({ target: { value } }) => setNotes(value)}
          />
        </FormControl>
      </Box>
      <Box
        component="div"
        display={"flex"}
        alignItems={"flex-start"}
        gap={"0.5rem"}
        paddingBottom={{ xs: "2rem", md: "3.5rem" }}
      >
        <Checkbox
          sx={{ fontSize: "24px" }}
          onChange={(event) => {
            setIsConsentAccepted(event.target.checked);
          }}
          onClick={(event) => {
            event.stopPropagation();
          }}
          icon={<span className="t-icon-checkbox-default"></span>}
          checkedIcon={<span className="t-icon t-icon-checkbox-checked"></span>}
        />
        <Typography component="p" variant="body1">
          {t("Gestiones.Doy mi consentimiento")}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          gap: "1rem",
          marginY: "1.5rem",
          flexWrap: "wrap",
        }}
      >
        <ButtonElement
          disabled={isDisabled}
          onClick={(event) => {
            event.preventDefault();
            handleSubmit(
              formatUpdatePowersRequestBody(
                checkBoxRadioValue,
                appliedDay?.$d,
                newPowers,
                notes
              )
            );
          }}
        >
          {t("Gestiones.Solicitar modificación de potencias")}
        </ButtonElement>
        <ButtonElement type="button" onClick={openModal} secondary>
          {t("Cancelar")}
        </ButtonElement>
      </Box>
      <ManagementsCancelProcess open={isModalOpen} handleClose={closeModal} />
    </form>
  );
};

export default ManagementsUpdateNewPowers;
